import { inject, Injectable } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { BaseSiteService, Country, Language, SiteContextActions } from '@spartacus/core';
import { SiteContextComponentService, SiteContextType } from '@spartacus/storefront';
import Cookies from 'js-cookie';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';

import { DpMarketSelectorService } from './market-selector.service';
import { DpGtmService } from '@app/core/services/gtm.service';
import { DpSiteContextCookiesService } from '@app-modules/site-context/services/site-context-cookies.service';
import { changeCountryAction } from '@app-modules/site-context/store/actions/change-country.action';
import {
  activeCountryCodeSelector,
  countriesSelector,
  languagesSelector
} from '@app-modules/site-context/store/site-context.selectors';
import { DpGoogleTagManagerService } from '@app-shared/google-tag-manager';

import { loadCountriesAction, loadLanguagesAction } from '../store/actions';

@Injectable({ providedIn: 'root' })
export class DpSiteContextService {
  readonly languages$: Observable<Language[]>;

  readonly #store = inject(Store);
  readonly #baseSiteService = inject(BaseSiteService);
  readonly #siteContextComponentService = inject(SiteContextComponentService);
  readonly #marketService = inject(DpMarketSelectorService);
  readonly #dpGtmService = inject(DpGtmService);
  readonly #cookiesService = inject(DpSiteContextCookiesService);
  readonly #gtmService = inject(DpGoogleTagManagerService);

  constructor() {
    this.#siteContextComponentService
      .getActiveItem(SiteContextType.LANGUAGE)
      .pipe(distinctUntilChanged())
      .subscribe(() => this.#store.dispatch(loadCountriesAction()));
    this.#baseSiteService
      .getActive()
      .pipe(distinctUntilChanged(), take(1))
      .subscribe((uid) => this.#store.dispatch(loadLanguagesAction({ payload: Cookies.get('market') ?? uid })));
    this.languages$ = this.#store.pipe(select(languagesSelector));
  }

  setActiveLanguage(language: Language): void {
    if (language?.isocode) {
      this.#cookiesService.setCookie(SiteContextType.LANGUAGE.toLowerCase(), language?.isocode);
      this.#siteContextComponentService.setActive(language?.isocode, SiteContextType.LANGUAGE);
      void this.#gtmService.pushTag({ event: 'languageSelector', languageSelect: language?.isocode });
    }
  }

  setActiveCountry(activeCountry: Country): void {
    if (activeCountry) {
      this.#marketService
        .setActiveCountry(activeCountry)
        .pipe(take(1))
        .subscribe(() => {
          this.#store.dispatch(new SiteContextActions.LoadLanguages());
          this.#store.dispatch(changeCountryAction());
          void this.#dpGtmService.initGtmPush();
        });
    }
  }

  getCountries$(): Observable<Country[]> {
    return this.#store.pipe(select(countriesSelector));
  }

  getActiveCountry$(): Observable<Country | undefined> {
    return combineLatest([
      this.#store.pipe(select(countriesSelector)),
      this.#store.select(activeCountryCodeSelector)
    ]).pipe(
      map(
        ([countries, code]: [Country[], string | undefined]) =>
          countries.find((country) => country.isocode === code) ||
          countries.find((country) => country.isocode === Cookies.get('country')) ||
          countries.find((country) => country.isocode === 'UK')
      )
    );
  }

  getActiveLanguage$(): Observable<string> {
    return this.#siteContextComponentService.getActiveItem(SiteContextType.LANGUAGE);
  }

  getActiveLanguageLabel$(): Observable<string> {
    return combineLatest([
      this.#siteContextComponentService.getItems(SiteContextType.LANGUAGE),
      this.#siteContextComponentService.getActiveItem(SiteContextType.LANGUAGE)
    ]).pipe(
      map(([items, active]) => {
        const selected = (items as Language[]).find((el) => el.isocode === active);
        return `${selected?.nativeName} (${selected?.isocode?.toUpperCase()})`;
      })
    );
  }
}
